import { SLUG, VOLUME } from 'utils/endpoint'
import { api, CAS_DOMAIN, formatPrice, getClassi, scrollToRef } from 'utils'
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { Image } from 'react-components/atoms'
import Breadcrumbs from 'components/atomic/Breadcrumbs'
import Error from 'next/error'
import MetaSeo from 'components/atomic/MetaSeo'
import { useDispatch } from 'react-redux'
import { setItem } from 'containers/toolbar'
import { Button, TabGroup, JsonLd } from 'react-components/atoms'
import { useTrans } from 'hooks'
import { Bar, GenericPageTab, StickyActions, BoxBundle } from 'react-components/organisms'
import { BoxAttivaLibro, BoxContenutiLibro, ThumbedSlider } from 'react-components/molecules'
import { ArrowDown, ChevronLeft } from 'react-components/atoms/Icons'
import Link from 'next/link'
import orderBy from 'lodash/orderBy'
import PlaceholderReact from 'components/atomic/PlaceholderReact'
import Credits from 'components/atomic/Credits'
import { useRouter } from 'next/router'
import { CartContext } from 'utils/context'
import { useProdottiAsBundleQuery } from 'gql/graphql'
import Modal from 'react-components/atoms/Modal/Modal'
import { sendProductDetailGTM } from 'utils/gtm'

const VolumeDetail = ({ item, page, statusCode }) => {
  if (!item) return <Error statusCode={statusCode} />
  const bundleRef = useRef(null)

  const { data: prodottiAsBundle } = useProdottiAsBundleQuery({
    variables: {
      pk: Number(item?.prodotto?.pk),
    },
    fetchPolicy: 'cache-first',
  })

  const context = useContext(CartContext)

  const t = useTrans()

  const dispatch = useDispatch()

  const router = useRouter()

  const [volume, setVolume] = useState(item)

  const [anchor, setAnchor] = useState('')

  const [showModal, setShowModal] = useState(false)

  const refresh = async () => {
    const { data } = await api.get(`${VOLUME}${volume.id}/`)
    setVolume(data)
    sendProductDetailGTM(data, 'navigazione_standard')
    dispatch(setItem(data))
  }

  useEffect(() => {
    refresh()
  }, [])

  useEffect(() => {
    if (router.asPath) initAnchor()
  }, [router.asPath])

  function initAnchor() {
    const anchor = router.asPath.split('#')[1]
    if (anchor) setAnchor(anchor)
  }

  const parent =
    volume.corsoadozionale ||
    volume.parascolastico ||
    volume.libroinfanzia ||
    volume.libroauxilia ||
    volume.libroragazzi
  const adozionale = Boolean(volume.corsoadozionale)

  const immagini = [volume.main_image]
    .concat(orderBy(volume.gallery, ['ordinamento']).map((i) => i.image))
    .filter((i) => i)
    .slice(0, 6)

  const classe = getClassi(volume.tassonomia).join('-')

  const addItemToCart = (prodotto) => {
    context
      .addToCart(prodotto, 1, true, 'pdp', null, null, 'navigazione_standard')
      .then((res) => setShowModal(res.showMessage))
  }

  const tabRisorse = [volume.raccolte, volume.strumenti, volume.articoli].some((x) => x.length)
    ? [{ ...volume, titolo: 'Contenuti extra', slug: 'contenuti-extra' }]
    : []

  const tabs = useMemo(
    () =>
      volume.tabs.concat(tabRisorse).map((tab) => ({
        key: tab.slug,
        label: tab.titolo,
        content: (
          <GenericPageTab tab={tab}>
            <PlaceholderReact
              contentTypeId={volume.model}
              objectId={volume.id}
              pkey={`${volume.id}-${tab.slug}-content`}
              serverSideContent={null}
            />
          </GenericPageTab>
        ),
      })),
    [volume]
  )

  const prodotto = volume.prodotto

  return (
    <main className={`catalogo-page catalogo-page--volume`}>
      <Modal open={showModal} closeButton={true} setOpen={() => setShowModal(false)}>
        <Modal.Content
          title="Forzatura Vendita"
          description={volume.prodotto?.forzatura?.disclaimer}
        />
      </Modal>
      <MetaSeo seo={page.seo} />
      <JsonLd item={volume} />
      <div className="w-container">
        <Breadcrumbs breadcrumbs={page.breadcrumbs} />
      </div>
      <div className="w-container">
        <div className="catalogo-page__sticky-container">
          <StickyActions item={volume} labelGTM={'volume'} share />
        </div>
        <div className="catalogo-page__header-container">
          <div className="catalogo-page__header catalogo-page__header--volume">
            {parent && parent.url && (
              <Link legacyBehavior href={parent.url}>
                <a className="catalogo-page__back catalogo-page__back--mobile">
                  <ChevronLeft />
                  {t('Torna a')} {parent.titolo}
                </a>
              </Link>
            )}
            {volume.main_image && (
              <div className="catalogo-page__image-box">
                {immagini.length > 1 ? (
                  <ThumbedSlider
                    immagini={immagini}
                    altTitle={`${volume.titolo} ${volume.corsoadozionale ? 'libro di testo' : ''} ${
                      volume.sottocategoria ? volume.sottocategoria : ''
                    } ${classe ? classe : ''}`.toAltTitle()}
                    preload
                  />
                ) : (
                  <Image
                    src={CAS_DOMAIN + volume.main_image}
                    alt={`${volume.titolo} ${volume.corsoadozionale ? 'libro di testo' : ''} ${
                      volume.sottocategoria ? volume.sottocategoria : ''
                    } ${classe ? classe : ''}`.toAltTitle()}
                    title={volume.titolo.toAltTitle()}
                    width={285}
                    height={360}
                    priority
                  />
                )}
              </div>
            )}
            <div className="catalogo-page__summary">
              {parent && parent.url && (
                <Link legacyBehavior href={parent.url}>
                  <a className="catalogo-page__back catalogo-page__back--desktop">
                    <ChevronLeft />
                    {t('Torna a')} {parent.titolo}
                  </a>
                </Link>
              )}
              <h1 className="catalogo-page__title">{volume.titolo}</h1>
              <div className="catalogo-page__cta-box">
                {prodotto?.prezzoScontato && prodotto?.prezzo !== prodotto?.prezzoScontato && (
                  <span className="catalogo-page__price catalogo-page__price--full">
                    {formatPrice(prodotto?.prezzo)}
                  </span>
                )}
                {volume.prezzo && (
                  <span className="catalogo-page__price">
                    {formatPrice(prodotto?.prezzoScontato || volume.prezzo)}
                  </span>
                )}
                {(prodotto?.scontoPercentuale || prodotto?.scontoAssoluto) && (
                  <span className="catalogo-page__discount">
                    -
                    {prodotto?.scontoPercentuale ? (
                      <>{prodotto?.scontoPercentuale}%</>
                    ) : (
                      formatPrice(prodotto?.scontoAssoluto)
                    )}
                  </span>
                )}
                {prodotto && prodotto?.prezzoScontato ? (
                  <Button
                    className="catalogo-page__cta-box__cta"
                    label={prodotto.forzatura ? t('Pre-ordina ora') : t('Acquista')}
                    onClick={() => addItemToCart(prodotto)}
                    colorVariant={prodotto.forzatura ? 'darkblue' : ''}
                  />
                ) : (
                  volume.link_esterno && (
                    <Button
                      className="catalogo-page__cta-box__cta"
                      label={t('Acquista')}
                      href={volume.link_esterno}
                      external
                      newPage
                    />
                  )
                )}
                {prodotto &&
                  prodottiAsBundle &&
                  prodottiAsBundle?.prodottiAsBundle &&
                  prodottiAsBundle?.prodottiAsBundle?.prodottiAsBundle &&
                  prodottiAsBundle.prodottiAsBundle.prodottiAsBundle.length > 0 && (
                    <Button
                      className="catalogo-page__cta-box__cta"
                      label={t('Scopri accessori')}
                      onClick={() => scrollToRef(bundleRef)}
                      variant="secondary"
                    />
                  )}
                {volume.link_sfogliabile && (
                  <Button
                    className="catalogo-page__cta-box__cta catalogo-page__cta-box__cta--sfogliabile"
                    label={t('Guarda lo sfogliabile')}
                    href={volume.link_sfogliabile}
                    variant="secondary"
                    external
                    newPage
                  />
                )}
              </div>
              {prodotto && prodotto.forzatura && prodotto.forzatura.disclaimer && (
                <div className="catalogo-page__forzatura">{prodotto?.forzatura?.disclaimer}</div>
              )}
              <div className="catalogo-page__editore">
                Editore <b>{volume.editore || t('SOC. GIUNTI SCUOLA')}</b>
              </div>
              {volume.numero_pagine && (
                <div className="catalogo-page__pagine">
                  Numero pagine <b>{volume.numero_pagine}</b>
                </div>
              )}
              {classe && (
                <div className="catalogo-page__classe">
                  Classe <b>{classe}</b>
                </div>
              )}
              <div
                className="catalogo-page__abstract"
                dangerouslySetInnerHTML={{ __html: volume.descrizione }}
              />
              {volume.isbn && (
                <p className="catalogo-page__isbn">
                  {t('ISBN:')} {volume.isbn}
                </p>
              )}
              {parent && (
                <div className="catalogo-page__box-libro">
                  <BoxContenutiLibro
                    libro={{ box_contenuti_libro: volume.box_contenuti_libro, ...parent }}
                    className="catalogo-page__box-contenuti-libro"
                    adozionale={adozionale}
                  />
                  <BoxAttivaLibro
                    libro={{ box_attiva_libro: volume.box_attiva_libro, ...parent }}
                    className="catalogo-page__box-attiva-libro catalogo-page__box-attiva-libro--volume"
                    adozionale={adozionale}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="catalogo-page__tabs">
        <div className="w-container">
          <TabGroup
            tabs={tabs}
            initialKey={anchor}
            className="catalogo-page__tabgroup"
            variant="white"
          />
          {tabs.map((tab) => (
            <Credits
              label={tab.label}
              crediti={tab.content}
              classes="catalogo-page__credits"
              variant="catalogo"
              icon={<ArrowDown />}
              initialOpen={anchor === tab.key}
              href={`#${tab.key}`}
              key={tab.slug}
            />
          ))}
        </div>
        <div
          className="catalogo-page__bundle-container catalogo-page__bundle-container__volume-page"
          ref={bundleRef}
        >
          <div className="w-container">
            {prodottiAsBundle &&
              prodottiAsBundle?.prodottiAsBundle &&
              prodottiAsBundle?.prodottiAsBundle?.prodottiAsBundle &&
              prodottiAsBundle.prodottiAsBundle.prodottiAsBundle.length > 0 &&
              prodottiAsBundle?.prodottiAsBundle?.prodottiAsBundle.map((bundle) => (
                <BoxBundle bundle={bundle} />
              ))}
          </div>
        </div>
      </div>
      <Bar item={volume} share labelGTM={'volume'}>
        {prodotto && prodotto?.prezzoScontato ? (
          <Button
            label={prodotto.forzatura ? t('Pre-ordina ora') : t('Acquista')}
            onClick={() => addItemToCart(prodotto)}
            colorVariant={prodotto.forzatura ? 'darkblue' : ''}
          />
        ) : (
          volume.link_esterno && (
            <Button label={t('Acquista')} href={volume.link_esterno} external newPage />
          )
        )}
      </Bar>
    </main>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = async ({ params: { slug }, preview, previewData }) => {
  const headers = preview && previewData?.headers ? { cookie: previewData.headers } : {}

  const response = await api.get(`${VOLUME}${SLUG}${slug}/`, { params: { preview } }, { headers })
  const item = response.data

  if (!item) {
    return {
      notFound: true,
    }
  }

  const page = item.pagina

  const toolbarProps = { toolbarItem: item, toolbarPage: page }

  return {
    props: {
      item,
      page,
      ...toolbarProps,
      key: item.id,
      revalidate: 60 * 10, // trovare alternativa in stile data-fetching-wrapper di Luca D. per altre pagine senza dover passare manualmente il revalidate
    },
    revalidate: 60 * 10,
  }
}

export default VolumeDetail
